import { useState } from "react";
import SelectSearch from "../../common/forms/SelectSearch";
import useUserQuery from "../../common/lib/fetch/useUserQuery";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import QueryView from "../../common/lib/QueryView";
import SpinnerButton from "../../common/SpinnerButton";
import { adminFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import { humanReadableSort } from "../../common/utilities";
import useUpdateScaleSettings, { ScaleUsage, BayScaleSettingsDto, UpdateBayScaleSettingsDto, ReceivingType } from "../../requests/useUpdateScaleSettings";
import { User } from "../../userAccess/UserContext";
import { useUser } from "../../userAccess/useUser";

export interface ScaleAutomationSettings {
  minimumTareWeight: number | null;
  maximumTareWeight: number | null;
  minimumNetWeight: number | null;
  bays: BayScaleSettingsDto[];
}

const ScaleAutomation = () => {
  const displayMessage = useDisplayMessage();
  const user = useUser(); 

  const useGetScaleAutomationSettings = useUserQuery<ScaleAutomationSettings>({
    path: `/api/sites/${user.selectedSite!.id}/settings`
  }, {
    onError: (err) => displayMessage.fail(adminFacingErrorMessage(err))
  });

  return <QueryView
    query={useGetScaleAutomationSettings}
    renderData={response => <>
      {displayMessage?.message != null && <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />}
      <ScaleAutomationForm 
        bayScaleSettings={humanReadableSort(response.bays, "name")}
        minimumTareWeight={response.minimumTareWeight}
        maximumTareWeight={response.maximumTareWeight}
        minimumNetWeight={response.minimumNetWeight}
        user={user} />
    </>
  }/>
}

interface ScaleAutomationFormProps {
  bayScaleSettings: BayScaleSettingsDto[],
  minimumTareWeight: number | null,
  maximumTareWeight: number | null,
  minimumNetWeight: number | null,
  user: User
}

const ScaleAutomationForm = (props: ScaleAutomationFormProps) => {
  const displayMessage = useDisplayMessage();
  const [bayScaleSettings, setBayScaleSettings] = useState<BayScaleSettingsDto[]>(props.bayScaleSettings);

  const [validateTareWeight, setValidateTareWeight] = useState<boolean>(props.minimumTareWeight !== null || props.maximumTareWeight !== null);
  const [minTareWeight, setMinTareWeight] = useState<string | null>(props.minimumTareWeight?.toString() ?? null);
  const [maxTareWeight, setMaxTareWeight] = useState<string | null>(props.maximumTareWeight?.toString() ?? null)

  const [validateNetWeight, setValidateNetWeight] = useState<boolean>(props.minimumNetWeight !== null);
  const [minNetWeight, setMinNetWeight] = useState<string | null>(props.minimumNetWeight?.toString() ?? null);

  const updateScaleAutomationSettings = useUpdateScaleSettings(props.user, {
    onSuccess: () => displayMessage.success("Scale automation settings updated"),
    onError: (err) => displayMessage.fail(adminFacingErrorMessage(err))
  });

  const toggleTare = (ev: React.ChangeEvent<HTMLInputElement>, bayIndex: number) => {
    const updatedBayScaleSettings = JSON.parse(JSON.stringify(bayScaleSettings));
    updatedBayScaleSettings[bayIndex].scaleUsage = ev.target.checked ? ScaleUsage.Tare : ScaleUsage.None;
    setBayScaleSettings(updatedBayScaleSettings);
  }

  const toggleGross = (ev: React.ChangeEvent<HTMLInputElement>, bayIndex: number) => {
    const updatedBayScaleSettings = JSON.parse(JSON.stringify(bayScaleSettings));
    updatedBayScaleSettings[bayIndex].scaleUsage = ev.target.checked ? ScaleUsage.TareAndGross : ScaleUsage.Tare;
    setBayScaleSettings(updatedBayScaleSettings);
  }

  const updateReceivingType = (value: string, bayIndex: number) => {
    const newType = (value as unknown) as ReceivingType;
    const updatedBayScaleSettings = JSON.parse(JSON.stringify(bayScaleSettings));
    updatedBayScaleSettings[bayIndex].receivingType = newType;
    setBayScaleSettings(updatedBayScaleSettings);
  }

  const save = () => {
    const bayScaleSettingsUpdates = bayScaleSettings.map<UpdateBayScaleSettingsDto>(b => ({
      id: b.id,
      scaleUsage: b.scaleUsage,
      receivingType: b.receivingType,
    }));

    const weightsValidated = validateWeights();

    if (weightsValidated) {
      updateScaleAutomationSettings.request({
        minimumTareWeight: validateTareWeight ? Number(minTareWeight) : null,
        maximumTareWeight: validateTareWeight ? Number(maxTareWeight) : null,
        minimumNetWeight: validateNetWeight ? Number(minNetWeight) : null,
        bayScaleSettings: bayScaleSettingsUpdates
      });
    }
  }

  const validateWeights = () => {
    var failureMessage = null;

    if (validateTareWeight) {
      if (minTareWeight === '' || maxTareWeight === '') {
        failureMessage = 'Both minimum and maximum tare weight are required.'
      }
      else if (Number(minTareWeight) < 0 || Number(maxTareWeight) > 500_000) {
        failureMessage = 'Tare weight validation must be between 0 and 500,000 lbs.';
      }
      else if (Number(minTareWeight) >= Number(maxTareWeight)) {
        failureMessage = 'Minimum tare weight cannot be larger than maximum tare weight.';
      }
    }

    if (validateNetWeight) {
      if (minNetWeight === '') {
        failureMessage = 'Minimum net weight is required.'
      }
      else if (Number(minNetWeight) < 0 || Number(minNetWeight) > 100_000) {
        failureMessage = 'Minimum net weight must be between 0 and 100,000 lbs.';
      }
    }

    if (failureMessage !== null) {
      displayMessage.fail(failureMessage)
    }

    return failureMessage === null;
  }

  if (!bayScaleSettings.length) {
    return <>
      <div className="alert alert-dismissible alert-danger" role="alert">
        Current site does not have any bays 
      </div>
    </>
  } else { 
    return <>
      <div className="m-auto w-85 mb-3">
        {displayMessage?.message != null && <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />}
        <h2>Scale Automation</h2>
        <div className="border border-dark rounded w-75">
          <table className="table">
            <thead>
              <tr>
                <th className="col-2">Bay</th>
                <th className="col-2">Receiving</th>
                <th className="col-2">Check Inbound Weight</th>
                <th className="col-2">Check Outbound Weight</th>
              </tr>
            </thead>
            <tbody>
              {bayScaleSettings.map((bay, i) => 
                <tr key={bay.id}>
                  <td>{bay.name}</td>
                  <td>
                    <div className="m-auto">
                      <SelectSearch
                        id={`sel-receiving-${bay.name}`}
                        hideSearch={true}
                        onChange={(value) => updateReceivingType(value, i)}
                        selectedValue={bay.receivingType}
                        options={[
                          { value: ReceivingType.Scale, name: 'Scale' },
                          { value: ReceivingType.Loadpass, name: 'LOADPASS' },
                          { value: ReceivingType.BulkReceiving, name: "Bulk Receiving" }]} />
                    </div>
                  </td>
                  <td>
                    <div className="m-auto">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`cbx-tare-${bay.name}`}
                        onChange={(ev) => toggleTare(ev, i)}
                        checked={bay.scaleUsage !== ScaleUsage.None} />
                    </div>
                  </td>
                  <td>
                    <div className="align-center">
                      <input
                        disabled={bay.scaleUsage === ScaleUsage.None}
                        type="checkbox"
                        className="form-check-input"
                        id={`cbx-gross-${bay.name}`}
                        onChange={(ev) => toggleGross(ev, i)}
                        checked={bay.scaleUsage === ScaleUsage.TareAndGross} />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="border border-dark rounded w-75 my-3">
          <div className="m-3">
            <div className="row">
              <h4 className="col-auto">
                <i className="bi bi-exclamation-triangle-fill me-1 text-warning" />
                Tare Weight Range Validation
              </h4>
              <div className="col"> 
                <div className="float-end form-check form-switch">
                  <input
                    id={`tareWeightRangeValidation`}
                    type="checkbox"
                    className="form-check-input me-2"
                    onChange={() => setValidateTareWeight(!validateTareWeight)}
                    checked={validateTareWeight}
                  />
                  <label
                    className="form-check-label-lg"
                    htmlFor={`tareWeightRangeValidation`}>
                    Enable Validation
                  </label>
                </div>
              </div>
            </div>
            <p>Prevents drivers from capturing an incorrect tare weight after loading by validating that the tare weight falls within an acceptable range.</p>
            <label
              className="fw-bold"
              htmlFor={`minimumTareWeight`}>
              Minimum Tare Weight (lbs)
            </label>
            <div className="input-group input-group-sm w-75 mb-3">
              <input
                id={`minimumTareWeight`}
                type="number"
                className={`form-control`}
                value={minTareWeight ?? undefined}
                onChange={event => setMinTareWeight(event.target.value)}
                disabled={!validateTareWeight}
              />
            </div>
            <label
              className="fw-bold"
              htmlFor={`maximumTareWeight`}>
              Maximum Tare Weight (lbs)
            </label>
            <div className="input-group input-group-sm w-75">
              <input
                id={`maximumTareWeight`}
                type="number"
                className={`form-control`}
                value={maxTareWeight ?? undefined}
                onChange={event => setMaxTareWeight(event.target.value)}
                disabled={!validateTareWeight}
              />
            </div>
          </div>
        </div>
        <div className="border border-dark rounded w-75 my-3">
          <div className="m-3">
            <div className="row">
              <h4 className="col-auto">
                <i className="bi bi-exclamation-triangle-fill me-1 text-danger" />
                Minimum Net Weight Validation
              </h4>
              <div className="col"> 
                <div className="float-end form-check form-switch">
                  <input
                    id={`netWeightRangeValidation`}
                    type="checkbox"
                    className="form-check-input me-2"
                    onChange={() => setValidateNetWeight(!validateNetWeight)}
                    checked={validateNetWeight}
                  />
                  <label
                    className="form-check-label-lg"
                    htmlFor={`netWeightRangeValidation`}>
                    Enable Validation
                  </label>
                </div>
              </div>
            </div>
            <p>Identifies and prevents extremely low or zero net weights due to improper tare capture by ensuring the calculated net weight (gross - tare) meets minimum requirements.</p>
            <label className="fw-bold"
              htmlFor={`minimumNetWeight`}>
              Minimum Net Weight (lbs)
            </label>
            <div className="input-group input-group-sm w-75">
              <input
                type="number"
                id={`minimumNetWeight`}
                className={`form-control`}
                value={minNetWeight ?? undefined}
                onChange={event => setMinNetWeight(event.target.value)}
                disabled={!validateNetWeight}
              />
            </div>
            <p className="mt-2">Net Weight = Gross Weight - Tare Weight</p>
          </div>
        </div>
      </div>
      <div className="align-self-end">
        <SpinnerButton 
          className="btn btn-primary mx-4 mb-4 px-5 py-3" 
          spinning={updateScaleAutomationSettings.isLoading}
          onClick={save}>
          Save
        </SpinnerButton>
      </div>
    </>
  }
}

export { ScaleAutomation };
